<script setup>
import moment from 'moment'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useReservationsStore, useRestaurantsStore, useUserGroupsStore, useUsersStore, useAppModeStore } from "@/stores"
import { buildProfileUrl, getFullUrl, displayPhone } from '@/plugins/misc'
import { canUseRestaurantAdminMode } from '@/utils/appModeEligibility'
import { sanitizeHtml } from '@core/utils/sanitizeHtml'
import MemberFavorites from "@core/components/MemberFavorites.vue"
import { favoritesVisible } from '@/utils/memberProfileView'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'

const props = defineProps({
  member: {
    type: Object,
  },
  userGroup: {
    type: Object
  },
  publicView: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['update:modelValue', 'update:validInputs'])

// Initialize stores - REVERTED to commit #93 pattern
const usersStore = useUsersStore()
let myUser = null
let users = null;
({ myUser, users } = storeToRefs(usersStore))

let viewingDetailsMember = ref(null)
const userGroupsStore = useUserGroupsStore()
const appModeStore = useAppModeStore()
const isRestaurantAdminEligible = computed(() => canUseRestaurantAdminMode(myUser?.value))

const reservationsStore = useReservationsStore()
let myReservations = null;
({ myReservations } = storeToRefs(reservationsStore))

const restaurantsStore = useRestaurantsStore()
let viewingDetailsRestaurant = null
let viewingDetailsRestaurantTab = null;
({ viewingDetailsRestaurant, viewingDetailsRestaurantTab } = storeToRefs(restaurantsStore))
const viewingTips = ref(false)

const isViewAndShareProfileDialogOpen = ref(false)
const isFollowersAndFollowingDialogOpen = ref(false)
const isUnfriendConfirmDialogOpen = ref(false)

const followersAndFollowingDialogTab = ref('Followers')

const favoritesAllOrBooked = ref("Booked")

const bookingsCount = computed(() => {
  //count myReservations where status="Accepted"
  return myReservations.value.filter(r => r.status === 'Accepted').length
})

const favoritesCount = computed(() => {
  // Use the backend-calculated count for consistency
  // Backend calculates this by querying: Restaurant.find({ favoritedBy: user._id })
  return props.member?.favoritedRestaurantsCount || 0
})

const route = useRoute()
const isMyProfile = computed(() => {
  if (route.query.public !== undefined) return false

  return myUser?.value?._id === props.member._id
})

const isFollowing = computed(() => {
  return myUser?.value?.following?.includes(props.member._id)
})

const isFollowRequestPending = computed(() => {
  if (!props.member?._id) return false

  return !!props.member?.followedByMyUserPending || (isFollowing.value && !followerApproved(props.member._id))
})

const isFriend = computed(() => {
  if (!props.member?._id) return false

  return isFollowing.value && followerApproved(props.member._id)
})

// Spouse/secondary pairs are permanently connected (hard-locked server-side) —
// show a non-actionable indicator instead of the unfriend control.
const isSpouse = computed(() => !!usersStore.mySpouseId && String(props.member?._id) === usersStore.mySpouseId)

const followedByMyUser = computed(() => {
  return users.value.filter(u => (u.followedByMyUser && !u.followedByMyUserPending))
})

const followedByMyUserPending = computed(() => {
  return users.value.filter(u => u.followedByMyUserPending)
})
const isFollowedByMyUser = userId => {
  return followedByMyUser.value.find(u => u._id == userId)
}
const isFollowedByMyUserPending = userId => {
  return followedByMyUserPending.value.find(u => u._id == userId)
}

const followerApproved = userId => {
  return myUser?.value?.followersApproved?.includes(userId)
}

const hasPendingNetworkRequests = computed(() => {
  let followingMyUserPendings = users.value.filter(u => u.followingMyUser && !followerApproved(u._id))

  return followingMyUserPendings.length > 0
})

const membersFollowed = computed(() => {
  if (!props?.member?.membersFollowed) return
  let followedUserIds = props.member.membersFollowed.map(m => m._id)
  let followedUsers = users.value.filter(u => followedUserIds.includes(u._id))

  followedUsers.sort((a, b) => {
    if (a.firstName < b.firstName) return -1
    if (a.firstName > b.firstName) return 1

    return 0
  })

  return followedUsers
})
const membersWhoAreFollowing = computed(() => {
  if (!props?.member?.membersWhoAreFollowing) return
  let membersWhoAreFollowingUserIds = props.member.membersWhoAreFollowing.map(m => m._id)
  let membersWhoAreFollowingUsers = users.value.filter(u => membersWhoAreFollowingUserIds.includes(u._id))

  membersWhoAreFollowingUsers.sort((a, b) => {
    if (a.firstName < b.firstName) return -1
    if (a.firstName > b.firstName) return 1

    return 0
  })

  return membersWhoAreFollowingUsers
})

const clearReactivationMessage = () => {
  myUser.value.clearReactivationMessage = true
  usersStore.updateMyUser()
}

const isProfileSummaryDialogOpen = ref(false)
const profileSummaryRef = ref(null)
const copyProfileSuccess = ref(false)
const copyProfileSummary = async () => {
  const el = profileSummaryRef.value
  if (!el) return

  const html = el.innerHTML
  const text = el.innerText

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' })
      })
    ])
    copyProfileSuccess.value = true
    setTimeout(() => { copyProfileSuccess.value = false }, 2000)
  } catch (err) {
    try {
      await navigator.clipboard.writeText(text)
      copyProfileSuccess.value = true
      setTimeout(() => { copyProfileSuccess.value = false }, 2000)
    } catch (e) {
      console.error('Copy failed:', e)
    }
  }
}

//when member changes run usersStore.fetchUserById()
watch(() => props.member, async (newVal, oldVal) => {
  if (newVal && myUser.value && (newVal?._id != oldVal?._id)) {
    usersStore.showLoadingOverlay()
    await usersStore.fetchUserById(newVal._id)
    usersStore.hideLoadingOverlay()
  }
}, { immediate: true })
</script>


<template>
  <div>
    <div class="profile-container content-card position-relative">
      <ViewAndShareProfileDialog v-model:is-dialog-visible="isViewAndShareProfileDialogOpen" />
      <!-- Unfriend Confirmation Dialog -->
      <VDialog
        v-model="isUnfriendConfirmDialogOpen"
        max-width="400"
        :z-index="3000"
      >
        <VCard>
          <VCardTitle class="text-h6 pa-4">
            Remove Friend
          </VCardTitle>
          <VCardText>
            Are you sure you want to remove {{ member.firstName }} {{ member.lastName }} as a friend?
          </VCardText>
          <VCardActions class="pa-4 pt-0">
            <VSpacer />
            <VBtn
              variant="text"
              @click="isUnfriendConfirmDialogOpen = false"
            >
              Cancel
            </VBtn>
            <VBtn
              color="error"
              variant="tonal"
              @click="usersStore.unfollow(member._id); isUnfriendConfirmDialogOpen = false"
            >
              Remove
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>
      <!-- Profile Summary Dialog -->
      <VDialog
        v-model="isProfileSummaryDialogOpen"
        max-width="520"
      >
        <VCard class="profile-summary-dialog">
          <div class="profile-summary-dialog-header">
            <div class="profile-summary-dialog-title">
              Shared with your reservation requests
            </div>
            <VBtn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="isProfileSummaryDialogOpen = false"
            />
          </div>
          <VDivider />
          <VCardText class="pa-6">
            <div class="d-flex align-center mb-4">
              <div class="profile-summary-avatar rounded-circle mr-3">
                <VImg
                  v-if="member.profileImageId"
                  class="profile-summary-avatar-img"
                  :src="mediaUrl(member.profileImageId, 'small')"
                  cover
                />
                <div
                  v-else
                  class="profile-summary-avatar-fallback d-flex align-center justify-center"
                >
                  <span>{{ member.firstName?.[0]?.toUpperCase() }}{{ member.lastName?.[0]?.toUpperCase() }}</span>
                </div>
              </div>
              <div class="profile-summary-name">
                {{ member.firstName }} {{ member.lastName }}
              </div>
            </div>
            <div
              ref="profileSummaryRef"
              class="profile-summary-content"
            >
              <div>
                <strong>{{ member.firstName }} {{ member.lastName }} - RM Profile Details</strong>
              </div>
              <div
                v-if="member.membershipType"
                class="profile-summary-line"
              >
                ({{ member.verified ? 'Verified + ' : '' }}{{ member.membershipType }} member)
              </div>
              <div
                v-if="member.phoneNumber"
                class="profile-summary-line"
              >
                <strong>M.</strong> {{ displayPhone(member.phoneNumber) }}
              </div>
              <div
                v-if="member.email"
                class="profile-summary-line"
              >
                <strong>E.</strong> {{ member.email }}
              </div>
              <div
                v-if="member.title || member.company"
                class="profile-summary-line"
              >
                <template v-if="member.title && member.company">
                  {{ member.title }}, {{ member.company }}
                </template>
                <template v-else>
                  {{ member.title || member.company }}
                </template>
              </div>
              <div
                v-if="member.title2 || member.company2"
                class="profile-summary-line"
              >
                <template v-if="member.title2 && member.company2">
                  {{ member.title2 }}, {{ member.company2 }}
                </template>
                <template v-else>
                  {{ member.title2 || member.company2 }}
                </template>
              </div>
              <div
                v-if="member.bio"
                class="profile-summary-line mt-3"
              >
                <strong>Bio:</strong> {{ member.bio }}
              </div>
              <div
                v-if="member.verifiedTags?.length > 0"
                class="profile-summary-tags mt-3"
              >
                <div
                  v-for="tag in member.verifiedTags"
                  :key="tag._id || tag.tagName"
                  class="profile-summary-tag-line"
                >
                  <strong>{{ tag.tagName }}:</strong> <span
                    v-html="sanitizeHtml(tag.tagShortDescription || tag.tagDescription || 'Verified by RM')"
                  />
                </div>
              </div>
              <div
                v-if="member.profileLinkedInProfileId || member.profileInstagramProfileId || member.profileXProfileId || member.profileTikTokProfileId || member.profileWebsiteLink || member.profileId"
                class="profile-summary-links mt-3"
              >
                <div
                  v-if="member.profileLinkedInProfileId"
                  class="profile-summary-link-line"
                >
                  <strong>LinkedIn: </strong>
                  <a
                    :href="buildProfileUrl('linkedin', member.profileLinkedInProfileId)"
                    target="_blank"
                    @click.stop=""
                  >linkedin.com/in/{{ member.profileLinkedInProfileId }}</a>
                </div>
                <div
                  v-if="member.profileInstagramProfileId"
                  class="profile-summary-link-line"
                >
                  <strong>Instagram:</strong> <a
                    :href="buildProfileUrl('instagram', member.profileInstagramProfileId)"
                    target="_blank"
                    @click.stop=""
                  >instagram.com/{{ member.profileInstagramProfileId }}</a>
                </div>
                <div
                  v-if="member.profileXProfileId"
                  class="profile-summary-link-line"
                >
                  <strong>X:</strong> <a
                    :href="buildProfileUrl('twitter', member.profileXProfileId)"
                    target="_blank"
                    @click.stop=""
                  >x.com/{{ member.profileXProfileId }}</a>
                </div>
                <div
                  v-if="member.profileTikTokProfileId"
                  class="profile-summary-link-line"
                >
                  <strong>TikTok:</strong> <a
                    :href="buildProfileUrl('tiktok', member.profileTikTokProfileId)"
                    target="_blank"
                    @click.stop=""
                  >tiktok.com/@{{ member.profileTikTokProfileId }}</a>
                </div>
                <div
                  v-if="member.profileWebsiteLink"
                  class="profile-summary-link-line"
                >
                  <strong>Bio Page: </strong> <a
                    :href="getFullUrl(member.profileWebsiteLink)"
                    target="_blank"
                    @click.stop=""
                  >{{
                    member.profileWebsiteLink }}</a>
                </div>
                <div
                  v-if="member.profileId"
                  class="profile-summary-link-line"
                >
                  <strong>RM: </strong> <a
                    :href="`/m/${member.profileId}?public`"
                    target="_blank"
                    @click.stop=""
                  >reserverm.com/m/{{
                    member.profileId }}</a>
                </div>
              </div>
            </div>
            <div class="profile-summary-content">
              <div
                v-if="member.profileId"
                class="profile-summary-link-line"
              >
                <a
                  :href="`/m/${member.profileId}?public`"
                  target="_blank"
                  @click.stop=""
                >(public view)</a>
              </div>
            </div>
          </VCardText>
          <VDivider />
          <VCardActions class="pa-4">
            <VSpacer />
            <VBtn
              variant="outlined"
              size="small"
              @click="isProfileSummaryDialogOpen = false"
            >
              Close
            </VBtn>
            <VBtn
              :color="copyProfileSuccess ? 'success' : 'primary'"
              :prepend-icon="copyProfileSuccess ? 'mdi-check' : 'mdi-content-copy'"
              size="small"
              @click="copyProfileSummary"
            >
              {{ copyProfileSuccess ? 'Copied!' : 'Copy Profile' }}
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>
      <VDialog
        v-model="viewingTips"
        persistent
        fullscreen
        no-click-animation
        :retain-focus="false"
        scroll-strategy="none"
        max-width="600px"
      >
        <VCard class="pa-2 pb-1 pa-md-6 pb-md-2">
          <VCardTitle class="headline d-flex align-center mt-2">
            <VIcon
              size="22"
              class="mr-1"
            >
              mdi-lightbulb-outline
            </VIcon>Profile Tips
          </VCardTitle>
          <VCardText>
            When you complete your profile, we can better represent you to restaurants and help you get more bookings.
            Here
            are a few tips.
          </VCardText>
          <VCardActions class="d-flex gap-3">
            <VSpacer />
            <VBtn
              variant="tonal"
              @click="viewingTips = false"
            >
              I'll do it later
            </VBtn>
            <VBtn
              variant="flat"
              @click="viewingTips = false"
            >
              Start Editing
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>
      <div class="profile-header">
        <div class="flex flex-column align-center">
          <div
            class="avatar rounded-circle cursor-pointer"
            @click="usersStore.editMyUser()"
          >
            <VImg
              v-if="member.profileImageId"
              class="avatar-image"
              :src="mediaUrl(member.profileImageId, 'small')"
              cover
            />
            <div
              v-else
              class="avatar-fallback flex flex-row align-center justify-center"
            >
              <span class="avatar-fallback-text">{{ member?.firstName?.[0]?.toUpperCase() }}{{
                member?.lastName?.[0]?.toUpperCase() }}</span>
            </div>
          </div>
        </div>
        <div class="profile-header-details">
          <div
            class="text-md font-weight-semibold"
            style="line-height: 1.2em;"
          >
            <a
              :href="`/m/${member?.profileId}`"
              target="_blank"
              style="text-decoration: inherit; color: inherit;"
            >
              {{ member.fullName }}
              <VIcon
                v-if="member.membershipType === 'Platinum' && member?.verified"
                size="20"
                class="verified-icon"
                style="margin: 0px; margin-top: -4px;"
              >
                material-symbols:verified
              </VIcon>
            </a>
            <span
              v-if="(member.membershipType && member.membershipType != 'Standard') || (isMyProfile && isRestaurantAdminEligible)"
              class="membership-tag-group"
            >
              <span
                v-if="member.membershipType && member.membershipType != 'Standard'"
                class="text-sm"
              >
                ({{ member.membershipType == 'Trial' ? 'Platinum' : member.membershipType }})
              </span>
              <VBtn
                v-if="isMyProfile && isRestaurantAdminEligible"
                variant="text"
                size="x-small"
                icon
                class="mode-switcher-trigger"
                :aria-label="`Switch app mode (currently ${appModeStore.mode === 'restaurantAdmin' ? 'Restaurant Admin' : 'Member'})`"
                @click="appModeStore.openDrawer()"
              >
                <VIcon size="20">
                  mdi-chevron-down
                </VIcon>
              </VBtn>
            </span>
          </div>

          <div
            v-if="member?.title && member?.company"
            class="text-sm mt-1"
          >
            {{ member?.title }} - {{ member?.company }}
          </div>
          <div
            v-else-if="member?.company"
            class="text-sm mt-1"
          >
            {{ member?.company }}
          </div>
          <div
            v-if="member?.title2 && member?.company2"
            class="text-sm"
          >
            {{ member?.title2 }} - {{ member?.company2 }}
          </div>
          <div
            v-else-if="member?.company2"
            class="text-sm"
          >
            {{ member?.company2 }}
          </div>
        </div>
      </div>
      <VCol class="pa-0">
        <div class="profile-info mt-4">
          <VCol
            class="pa-0"
            :class="{ 'text-decoration-line-through': member?.markedForDeletion }"
          >
            <span
              v-if="!member?.verified && member?.pendingVerification"
              class="verified-pending-text"
              @click="usersStore.showVerificationDialog()"
            >
              Verified status in review
            </span>
            <span
              v-else-if="!publicView && isMyProfile && !member?.platinumApproved && member?.pendingPlatinumApproval && member?.platinumPlanIdToGiveAfterPlatinumApproval && member?.verificationInfo1Data && !member?.promptToVerify"
              class="verified-pending-text"
              @click="usersStore.showVerificationDialog()"
            >
              Free Platinum membership in review
            </span>
            <span
              v-else-if="!member?.isPlatinum && !publicView && isMyProfile && !member?.platinumApproved && member?.pendingPlatinumApproval && !member?.platinumPlanIdToGiveAfterPlatinumApproval"
              class="verified-pending-text"
            >Platinum membership in review</span>
            <span
              v-if="member.membershipType === 'Platinum' && member?.verifiedTags?.length"
              class="mt-1 mb-1"
            >
              <MemberVerifiedTags :member="member" />
            </span>

            <div
              v-if="!publicView && isMyProfile"
              class="text-sm "
            >
              <template
                v-if="member.primaryGroupPermission == 'pending'"
                class="mt-1"
              >
                <div
                  v-if="!member.firstName || !member.lastName"
                  class=""
                >
                  Add profile to request approval from {{ member.primaryGroupName }}
                </div>
                <div
                  v-else
                  class=""
                >
                  Membership pending approval by {{ member.primaryGroupName }}
                </div>
              </template>
              <template
                v-else
                class="mt-1"
              >
                <div class="text-sm mb-0 ">
                  <template v-if="member.membershipExpiresSoon">
                    <span v-if="member.membershipExpiresSoon"> (exp {{ moment(member.platinumRenewalDate).subtract(1,
                                                                                                                   'day').format("M/D") }} - <RouterLink :to="{ name: 'membership' }">extend</RouterLink>)</span>
                  </template>
                </div>
                <template v-if="member?.isPlatinumTrial">
                  <div class="text-sm mb-0">
                    {{ member?.paidPlatinumReservations > 0 ? '(+' + member?.paidPlatinumReservations + ' Platinum Trial)' :
                      '' }}
                  </div>
                </template>
                <template v-else-if="member?.isPlatinumTrialPending">
                  <div class="text-sm mb-0">
                    (Platinum Trial Pending)
                  </div>
                </template>
              </template>
            </div>
          </VCol>
        </div>

        <div
          v-if="member?.bio"
          class="text-sm user-bio mt-4"
        >
          {{ member.bio }}
        </div>
        <div
          v-if="!publicView && isMyProfile"
          class="profile-header-stats d-flex align-center mt-2 mb-1"
        >
          <RouterLink
            v-if="isMyProfile || favoritesCount"
            to="/favorites"
            style="color: inherit;text-decoration: none;"
          >
            <div class="d-flex align-center gap-1">
              <span class="font-weight-semibold">{{ favoritesCount || '-' }}</span>
              <span class="text-xs">Favorites</span>
            </div>
          </RouterLink>
          <span
            v-if="isMyProfile || (member?.membersWhoAreFollowingCount && favoritesCount)"
            style="margin: 0px 4px; color: rgb(153, 153, 153);"
          />
          <RouterLink
            v-if="isMyProfile || member?.membersWhoAreFollowingCount"
            to="/network"
            style="color: inherit;text-decoration: none;"
          >
            <div class="d-flex align-center px-1 gap-1">
              <VBadge
                dot
                v-bind="hasPendingNetworkRequests"
                :model-value="hasPendingNetworkRequests"
                color="error"
                bordered
                offset-x="-6"
                offset-y="-4"
              >
                <span class="font-weight-semibold">{{ member?.membersWhoAreFollowingCount || '-'
                }}</span>
              </VBadge>
              <span class="text-xs">Connections</span>
            </div>
          </RouterLink>
        </div>
        <div
          v-else
          class="profile-header-stats flex align-start mt-2 mb-1"
        >
          <div
            v-if="member?.favoritedRestaurantsCount"
            class="d-flex align-center px-1 gap-1"
          >
            <span class="font-weight-semibold">{{ member?.favoritedRestaurantsCount || '-' }}</span>
            <span class="text-xs">Favorites</span>
          </div>
          <span
            v-if="member?.favoritedRestaurantsCount"
            style="margin: 0px 4px; color: rgb(153, 153, 153);"
          />
          <div
            class="d-flex align-center px-1 gap-1"
            :class="{ 'cursor-pointer': isMyProfile || isFollowing }"
            @click="(isMyProfile || isFollowing) && (isFollowersAndFollowingDialogOpen = true)"
          >
            <span class="font-weight-semibold">{{ member?.membersWhoAreFollowingCount || '-' }}</span>
            <span class="text-xs">Connections</span>
          </div>
        </div>
        <div class="mt-0 pt-0 pt-0">
          <VRow class="text-sm mt-0">
            <VCol class="d-flex align-center pt-1">
              <a
                v-if="member?.profileLinkedInProfileId"
                :href="buildProfileUrl('linkedin', member?.profileLinkedInProfileId)"
                target="_blank"
              >
                <VIcon
                  size="20"
                  class="mr-3"
                >mdi-linkedin</VIcon>
              </a>
              <a
                v-if="member?.profileInstagramProfileId"
                :href="buildProfileUrl('instagram', member?.profileInstagramProfileId)"
                target="_blank"
              >
                <VIcon
                  size="20"
                  class="mr-3"
                >mdi-instagram</VIcon>
              </a>
              <a
                v-if="member?.profileXProfileId"
                :href="buildProfileUrl('twitter', member?.profileXProfileId)"
                target="_blank"
              >
                <VIcon
                  size="20"
                  class="mr-3"
                >simple-icons:x</VIcon>
              </a>
              <a
                v-if="member?.profileTikTokProfileId"
                :href="buildProfileUrl('tiktok', member?.profileTikTokProfileId)"
                target="_blank"
              >
                <VIcon
                  size="20"
                  class="mr-3"
                >simple-icons:tiktok</VIcon>
              </a>
              <a
                v-if="member?.profileWebsiteLink"
                :href="getFullUrl(member?.profileWebsiteLink)"
                target="_blank"
              >
                <VIcon
                  size="20"
                  class="mr-3"
                >mdi-web</VIcon>
              </a>
            </VCol>

            <VSpacer />
            <VSpacer />
            <VSpacer />
            <VSpacer />

            <VCol>
              <VTooltip
                text="Your profile is important part of our process, and helps us represent you to restaurants. We recommend you take the time to complete you profile before submitting requests."
              >
                <template #activator="{ props }" />
              </VTooltip>
            </VCol>
          </VRow>
          <VRow class="text-sm mt-2 mb-1">
            <div
              v-if="isMyProfile && !publicView"
              class="profile-header-edit-button-holder"
            >
              <VBtn
                v-if="myUser?.markedForDeletion"
                variant="text"
                size="small"
                width="fit-content"
                density="compact"
                color="error"
                class="profile-header-edit-button"
                @click="usersStore.editMyUser()"
              >
                Account Deletion in Progress
              </VBtn>
              <template v-else>
                <VBtn
                  size="small"
                  width="fit-content"
                  class="profile-header-edit-button"
                  variant="text"
                  density="compact"
                  v-bind="props"
                  @click="usersStore.editMyUser()"
                >
                  Edit profile
                </VBtn>
                <VBtn
                  size="small"
                  width="fit-content"
                  class="profile-header-edit-button"
                  variant="text"
                  density="compact"
                  @click="isProfileSummaryDialogOpen = true"
                >
                  View Profile
                </VBtn>
                <br>
                <template v-if="myUser?.platinumMembershipStatus == 'expired' && !myUser?.clearReactivationMessage" />
              </template>
            </div>
            <div
              v-else-if="myUser"
              class="profile-header-edit-button-holder ml-1"
            >
              <VChip
                v-if="isSpouse"
                variant="tonal"
                size="small"
                color="primary"
                class="mx-2"
              >
                Spouse
              </VChip>
              <VBtn
                v-else-if="isFollowRequestPending"
                variant="tonal"
                size="small"
                width="fit-content"
                density="compact"
                color="secondary"
                class="mx-2"
                style="padding-left: 5px !important; padding-right: 5px !important; width: 90px; height: 28px;"
                @click="usersStore.unfollow(member._id)"
              >
                Requested
              </VBtn>
              <VBtn
                v-else-if="isFriend"
                variant="tonal"
                size="small"
                color="primary"
                class="mx-2"
                @click="isUnfriendConfirmDialogOpen = true"
              >
                Friend
              </VBtn>
              <VBtn
                v-else-if="myUser._id != member._id"
                variant="tonal"
                size="large"
                width="fit-content"
                density="compact"
                class="mx-2"
                style="padding-left: 12px !important; padding-right: 12px !important;"
                prepend-icon="mdi-account-plus"
                @click="usersStore.follow(member._id)"
              >
                Add Friend
              </VBtn>
            </div>
          </VRow>
        </div>
      </VCol>
    </div>

    <!--
      A friend sees a member's Favorites and, under each one, the note they
      chose to share — nothing else, no tabs (Kapil, 2026-09-22). A shared
      note's own home is the restaurant's Member Highlights tab. The member's
      own profile does not list them: their Favorites page already does.
    -->
    <!-- Centered at the profile card's own width, so the two read as one column. -->
    <MemberFavorites
      v-if="!isMyProfile && favoritesVisible(member)"
      :member="member"
      class="favorites-container"
    />

    <VDialog
      v-model="isFollowersAndFollowingDialogOpen"
      :width="$vuetify.display.mdAndDown ? '100%' : 650"
      persistent
      fullscreen
      no-click-animation
      :retain-focus="false"
    >
      <VCard class="">
        <VToolbar density="compact">
          <VToolbarItems>
            <VBtn
              icon="$close"
              size="large"
              variant="text"
              @click="isFollowersAndFollowingDialogOpen = false"
            >
              <VIcon>mdi-chevron-left</VIcon>
            </VBtn>
          </VToolbarItems>
          <VSpacer />
          <span>{{ props.member.firstName }} {{ props.member.lastName }}</span>
          <VSpacer />
          <VToolbarItems style="width: 58px" />
        </VToolbar>

        <div class="max-content-width position-relative">
          <VTabs
            v-model="followersAndFollowingDialogTab"
            class="tab-height"
            center-active
          >
            <VTab
              density="comfortable"
              value="Followers"
              class="tab-height"
            >
              Followers ({{ membersWhoAreFollowing?.length }})
            </VTab>
            <VTab
              density="comfortable"
              value="Following"
              class="tab-height"
            >
              Following ({{ membersFollowed?.length }})
            </VTab>
          </VTabs>
          <div
            v-if="false"
            class="reservations-loading-spinner"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
          </div>
          <VDivider />
          <VWindow v-model="followersAndFollowingDialogTab">
            <VWindowItem
              class="pb-3"
              value="Followers"
            >
              <VVirtualScroll
                :items="membersWhoAreFollowing"
                max-height="600"
                class="mt-2"
              >
                <template #default="{ item }">
                  <VListItem
                    :prepend-avatar="mediaUrl(item.profileImageId, 'thumb')"
                    class="pl-1 pr-0"
                    @click="viewingDetailsMember = item;"
                  >
                    <template
                      v-if="!item.profileImageId && (item?.firstName?.length > 0 || item?.lastName?.length > 0)"
                      #prepend
                    >
                      <div class="avatar-list rounded-circle">
                        <div class="avatar-fallback-list flex flex-row align-center justify-center">
                          <span class="avatar-fallback-text">{{ item?.firstName?.[0]?.toUpperCase() }}{{
                            item?.lastName?.[0]?.toUpperCase() }}</span>
                        </div>
                      </div>
                    </template>
                    <template #title>
                      <span class="text-sm">
                        {{ item.firstName + ' ' + item.lastName }}
                      </span>
                    </template>
                    <template #subtitle>
                      <span class="text-xs">
                        {{ (item.company ? item.company : '') }}
                      </span>
                    </template>
                    <template #append>
                      <VBtn
                        v-if="isFollowedByMyUserPending(item._id)"
                        size="small"
                        variant="tonal"
                        density="compact"
                        color="secondary"
                        style="padding-left: 5px !important; padding-right: 5px !important; width: 90px; height: 28px;"
                        @click.stop="usersStore.unfollow(item._id);"
                      >
                        Requested
                      </VBtn>
                      <VBtn
                        v-else-if="isFollowedByMyUser(item._id)"
                        size="small"
                        variant="tonal"
                        density="compact"
                        color="secondary"
                        style="padding-left: 5px !important; padding-right: 5px !important;"
                        @click.stop="usersStore.unfollow(item._id);"
                      >
                        Following
                      </VBtn>
                      <VBtn
                        v-else-if="myUser._id != item._id"
                        size="small"
                        variant="tonal"
                        density="compact"
                        color="primary"
                        style="padding-left: 5px !important; padding-right: 5px !important;"
                        @click.stop="usersStore.follow(item._id);"
                      >
                        Follow
                      </VBtn>
                    </template>
                  </VListItem>
                </template>
              </VVirtualScroll>
            </VWindowItem>
            <VWindowItem
              class="pb-3"
              value="Following"
            >
              <VVirtualScroll
                :items="membersFollowed"
                max-height="600"
                class="mt-2"
              >
                <template #default="{ item }">
                  <VListItem
                    :subtitle="`${(item.company ? item.company : '')}`"
                    :title="item.firstName + ' ' + item.lastName"
                    :prepend-avatar="mediaUrl(item.profileImageId, 'thumb')"
                    class="pl-1 pr-0"
                    @click="viewingDetailsMember = item;"
                  >
                    <template #title>
                      <span class="text-sm">
                        {{ item.firstName + ' ' + item.lastName }}
                      </span>
                    </template>
                    <template #subtitle>
                      <span class="text-xs">
                        {{ (item.company ? item.company : '') }}
                      </span>
                    </template>
                    <template #append>
                      <VBtn
                        v-if="isFollowedByMyUserPending(item._id)"
                        variant="tonal"
                        density="compact"
                        color="secondary"
                        style="padding-left: 5px !important; padding-right: 5px !important; width: 90px; height: 28px;"
                        @click.stop="usersStore.unfollow(item._id);"
                      >
                        Requested
                      </VBtn>
                      <VBtn
                        v-else-if="isFollowedByMyUser(item._id)"
                        size="small"
                        variant="tonal"
                        density="compact"
                        color="secondary"
                        style="padding-left: 5px !important; padding-right: 5px !important;"
                        @click.stop="usersStore.unfollow(item._id);"
                      >
                        Following
                      </VBtn>
                      <VBtn
                        v-else-if="myUser._id != item._id"
                        size="small"
                        variant="tonal"
                        density="compact"
                        color="primary"
                        style="padding-left: 5px !important; padding-right: 5px !important;"
                        @click.stop="usersStore.follow(item._id);"
                      >
                        Follow
                      </VBtn>
                    </template>
                    <template
                      v-if="!item.profileImageId && (item?.firstName?.length > 0 || item?.lastName?.length > 0)"
                      #prepend
                    >
                      <div class="avatar-list rounded-circle">
                        <div class="avatar-fallback-list flex flex-row align-center justify-center">
                          <span class="avatar-fallback-text">{{ item?.firstName?.[0]?.toUpperCase() }}{{
                            item?.lastName?.[0]?.toUpperCase() }}</span>
                        </div>
                      </div>
                    </template>
                  </VListItem>
                </template>
              </VVirtualScroll>
            </VWindowItem>
          </VWindow>
          <MemberDetailsDialog :member="viewingDetailsMember" />
        </div>
        <div class="d-flex flex-column align-center justify-center">
          <p class="text-center text-caption">
            <RouterLink
              class="text-primary"
              :to="{ path: '/members' }"
            >
              Members
            </RouterLink>&nbsp;&nbsp;|&nbsp;
            <RouterLink
              class="text-primary"
              :to="{ path: '/restaurants' }"
            >
              Restaurants
            </RouterLink>
          </p>
        </div>
      </VCard>
    </VDialog>
  </div>
</template>


<style lang="scss" scoped>
.favorites-container {
  //max-width: 900px;
  max-width: 460px;
  margin: 1rem auto 0.5rem;
  //gap: 2rem;
  border: none;
  //border-radius: 0.4rem;
}

.profile-container {
  display: flex;
  flex-direction: column;
  align-items: start;
  //max-width: 900px;
  max-width: 460px;
  margin: 0rem auto 0rem;
  //gap: 2rem;
  border: thin solid var(--ds-border-light);
  border-radius: 0.4rem;
  padding: 0.5rem;
}

.profile-header {
  width: 100%;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}

.profile-header-details {
  flex: 1;
  min-width: 0;
  margin-top: 20px;
}

.avatar {
  width: 6rem;
  height: 6rem;
  background: var(--ds-brand-primary-light);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 0.25rem;
  margin: 0.3rem;
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  font-size: 1.6rem;
}

.avatar-image {
  border-radius: 50%;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-fallback-text {
  width: fit-content;
}

.profile-header-stats {
  gap: 0.4rem;

  // The count is the value — give it a strong colour so it dominates its label.
  // Previously both the number and its label inherited the same medium-emphasis
  // wash, flattening the hierarchy. Matches the reservation pre-submit preview
  // block's stat treatment (value strong, label muted).
  .font-weight-semibold {
    color: var(--ds-text-strong, #1a1a1a);
  }
}

.verified-icon {
  color: rgb(0, 149, 246);
  width: 1.25rem;
  height: 1.25rem;
  margin-bottom: 5px;
}

.verified-icon-pending {
  cursor: pointer;
  color: #ffd900;
  width: 1.25rem;
  height: 1.25rem;
}

.verified-pending-text {
  cursor: pointer;
  display: inline-block;
  padding: 0.1rem;
  color: #5596d6;
  font-size: 0.75rem;
  padding: 0.1em 0.5em;
  border-radius: 0.4em;
  margin-left: -0.5em;
}

.verified-pending-text:hover {
  color: #21609f;
  background: #f1f1f1;
}

.profile-info {
  display: flex;
  flex-direction: row;
  align-items: start;
  flex-wrap: wrap;
  gap: 1rem
}

.profile-info-stats {
  display: none;
}

.user-bio {
  max-width: 100ch;
  //flex-basis:30rem;
}

.restaurant-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}


.restaurant-image-holder {
  width: 6rem;
  height: 6rem;
}

.avatar-list {
  cursor: pointer;
  position: relative;
  width: 36px;
  height: 36px;
  background: var(--ds-brand-primary-light);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 2px;
  margin-inline-end: 16px;
  margin-left: 2px;
}

.avatar-fallback-list {
  width: 100%;
  height: 100%;
  font-size: 1rem;
}


.restaurant-image-holder-favorites {
  width: 6rem;
  height: 6rem;
}

.restaurant-image-holder {
  width: 100%;
  aspect-ratio: 16/7;
  background: #efefef;
  border-radius: 0;
  overflow: hidden;
  //display: none;
}

.no-reservations-empty-state {
  width: 100%;
  max-width: 200px;
  opacity: 0.8;
}

.profile-summary-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.profile-summary-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 16px 24px;
}

.profile-summary-dialog-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.profile-summary-content {
  font-size: 14px;
  color: #444;
  line-height: 1.7;
}

.profile-summary-avatar {
  width: 4.5rem;
  height: 4.5rem;
  flex-shrink: 0;
  background: var(--ds-brand-primary-light);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 0.2rem;
}

.profile-summary-avatar-img {
  border-radius: 50%;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-summary-avatar-fallback {
  width: 100%;
  height: 100%;
  font-size: 1.5rem;
  color: #666;
}

.profile-summary-name {
  font-size: 20px;
  font-weight: 600;
  color: #222;
}

.profile-summary-line {
  color: #444;
}

.profile-summary-tags {
  border-top: 1px solid #eee;
  padding-top: 12px;
}

.profile-summary-links {
  border-top: 1px solid #eee;
  padding-top: 12px;
}

.profile-summary-link-line {
  padding: 3px 0;
  color: #444;
}

.profile-summary-link-line a {
  color: #1a73e8;
  text-decoration: none;
}

.profile-summary-link-line a:hover {
  text-decoration: underline;
}

.profile-summary-tag-line {
  padding: 3px 0;
  color: #444;
}

.membership-tag-group {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
  margin-left: 4px;
}
</style>
